<template>
  <component
    :is="to ? 'router-link' : href ? 'a' : 'button'"
    :to="to"
    :href="href"
    :target="target"
    :type="(!to && !href) ? 'button' : undefined"
    :class="['button-component button--' + variant, $props.class]"
    :style="style"
    @click="$emit('click')"
    v-bind="$attrs"
  >
    <img
      v-if="image"
      :src="image"
      class="button-icon-image"
      role="icon"
      alt="icon"
    />

    <!-- Lucide Icon Start -->
    <component
      v-if="lucideIconComponent && (iconPosition === 'start' || iconPosition === undefined)"
      :is="lucideIconComponent"
      :class="['button-icon-lucide', text ? 'mr-2' : '']"
      :size="18"
    />

    <span class="text" v-if="text !== undefined && text !== ''" v-html="text"></span>

    <!-- Lucide Icon End -->
    <component
      v-if="lucideIconComponent && iconPosition === 'end'"
      :is="lucideIconComponent"
      :class="['button-icon-lucide', text ? 'ml-2' : '']"
    />
    <div class="point"></div>
  </component>
</template>

<script setup>
import { computed } from 'vue'
import * as LucideIcons from '@lucide/vue'

const props = defineProps({
  variant: {
    type: String,
    default: 'gray'
  },
  text: String,
  icon: String,
  image: String,
  href: String,
  iconPosition: String, // start or end
  size: { type: String, default: 'md' },
  to: String,
  target: {
    type: String,
    default: '_self'
  },
  class: String,
  style: [String, Object]
})

defineEmits(['click'])

const lucideIconComponent = computed(() => {
  if (!props.icon) return null
  return LucideIcons[props.icon] || null
})
</script>
<style lang="scss" scoped>
  @use "sass:color";
  @use "../../assets/styles/variables.scss" as *;
  /** GENERAL STYLES **/
  .button-component {
    margin: 0;
    width: auto;
    border-radius: 12px;
    position:relative;
    padding: 0.8rem 1.5rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid transparent;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    line-height: normal;

    .button-icon-image {
      height: 1.2rem;
      width: auto;
      margin-top: -2px;
    }

    .button-icon-lucide {
      width: 1.1rem;
      height: 1.1rem;
    }

    .text{
      font-size: 0.95rem;
      min-height: auto;
      font-weight: 500;
      text-align: center;
    }
    .icon{
      height: 1.2rem;
      max-height: 1.2rem;
      padding: 0.1rem;
    }
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
      transform: translateY(-1px);
      .point {
          top: -0.4rem;
          right: -0.4rem;
          opacity: 1;
      }
    }
    &:active {
      transform: translateY(0);
    }
    &:disabled {
      background-color: $color-light-gray;
      color: $color-black;
      opacity: 0.6;
      cursor: not-allowed;
      .point{
          background-color: $color-light-gray;
      }
    }
    .point {
      width: 0.6rem;
      min-width: 0.6rem;
      height: 0.6rem;
      border-radius: 50%;
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      transition: all 0.3s;
      opacity: 0;
    }
  }

  .button-component:focus{
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 109, 240, 0.2);
  }
  /** PRIMARY VARIANT **/
  .button--primary  {
    border: none !important;
    background-color: $color-blue !important;
    color: $color-white !important;
    .point{
        background-color: transparent;
    }
    &:hover {
        background-color: color.adjust($color-blue, $lightness: -10%) !important;
        box-shadow: 0 4px 15px rgba($color-blue, 0.3);
    }
  }

  /** OUTLINE GRAY VARIANT **/
  .button--outline-gray {
    border: 1px solid #e2e8f0 !important;
    background-color: #ffffff !important;
    color: #475569 !important;
    .point {
      background-color: transparent;
    }
    &:hover, &:focus, &:active, &.active {
      background-color: #f8fafc !important;
      border-color: #cbd5e1 !important;
      color: #1e293b !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
    }
  }

  /** WARNING VARIANT **/
  .button--warning {
    border: none !important;
    background-color: $color-yellow !important;
    color: #171e44 !important;
    font-weight: 600;
    .point {
      background-color: transparent;
    }
    &:hover {
      background-color: color.adjust($color-yellow, $lightness: -5%) !important;
      box-shadow: 0 4px 15px rgba($color-yellow, 0.3);
    }
  }

  /** GRAY VARIANT **/
  .button--gray {
    border: 1px solid $color-gray--dark !important;
    background-color: $color-gray--light !important;
    color: #475569 !important;
    .point {
      background-color: transparent;
    }
    &:hover, &:focus, &:active, &.active {
      background-color: $color-gray !important;
      border-color: color.adjust($color-gray--dark, $lightness: -10%) !important;
      color: #1e293b !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
    }
  }

  /** SECONDARY VARIANT **/
  .button--secondary {
    border: 1px solid $color-gray--dark !important;
    background-color: $color-white !important;
    color: #475569 !important;
    .point {
      background-color: transparent;
    }
    &:hover, &:focus, &:active, &.active {
      background-color: $color-gray--light !important;
      border-color: color.adjust($color-gray--dark, $lightness: -10%) !important;
      color: #1e293b !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
    }
  }

    /** GRAY VARIANT **/
    .button--gray {
      border: 1px solid $color-gray--dark !important;
      background-color: $color-gray--light !important;
      color: #475569 !important;
      .point {
        background-color: transparent;
      }
      &:hover, &:focus, &:active, &.active {
        background-color: $color-gray !important;
        border-color: color.adjust($color-gray--dark, $lightness: -10%) !important;
        color: #1e293b !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
      }
    }

    /** SECONDARY VARIANT **/
    .button--secondary {
      border: 1px solid $color-gray--dark !important;
      background-color: $color-white !important;
      color: #475569 !important;
      .point {
        background-color: transparent;
      }
      &:hover, &:focus, &:active, &.active {
        background-color: $color-gray--light !important;
        border-color: color.adjust($color-gray--dark, $lightness: -10%) !important;
        color: #1e293b !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
      }
    }

    /** DANGER VARIANT **/
    .button--danger {
      border: 0.2rem solid $color-red--dark !important;
      background-color: $color-red !important;
      color: $color-white !important;
      .point{
          background-color: transparent;
      }
      &:hover {
          color:$color-white !important;
          border: 0.2rem solid $color-red !important;
          background: $color-red--dark !important;
          .point{
            background-color: $color-red;
          }
      }
    }

    /** ORANGE VARIANT **/
    .button--orange {
      border: 2px solid rgb(255 128 40 / var(--tw-bg-opacity, 1)) !important;
      background-color: transparent !important;
      color: rgb(255 128 40 / var(--tw-bg-opacity, 1)) !important;
      .point{
          background-color: transparent;
      }
      &:hover, &:focus, &:active, &.active {
          color: #ffffff !important;
          background-color: rgb(255 128 40 / var(--tw-bg-opacity, 1)) !important;
          box-shadow: 0 4px 15px rgba(255, 128, 40, 0.3) !important;
      }
    }

  /** WARNING LIGHT VARIANT **/
  .button--warning-light {
    border: 0.2rem solid $color-orange !important;
    background-color: $color-yellow--light !important;
    color: $color-black--light !important;
    .point{
        background-color: transparent;
    }
    &:hover {
        color:$color-black !important;
        border: 0.2rem solid $color-orange !important;
        .point{
          background-color: $color-orange;
        }
    }
  }

  /** SUCCESS VARIANT **/
  .button--success {
    border: 0.2rem solid $color-green--dark !important;
    background-color: $color-green-light !important;
    color: $color-green--dark !important;
    .point{
        background-color: transparent;
    }
    &:hover {
        color:$color-green--dark !important;
        border: 0.2rem solid $color-green--dark !important;
        .point{
          background-color: $color-green--dark;
        }
    }
  }

/** FOR SMARTPHONES **/
@media (max-width: 768px) {
   .button-component{
      font-size: 0.8rem;
      margin: 0;
      width: 100%;
      border-radius: 12px;
      position:relative;
      padding-left: 0.4rem;
      padding-right: 0.4rem;
      .point {
        display:none;
      }
     .text{
       font-size:0.8rem;
     }
      .icon{
        display:none;
      }
   }
}
/** FOR TABLETS AND IPADS **/
@media (max-width: 1280px) {
  .button-component{
    font-size: 1rem;
    margin: 0;
    width: 100%;
    border-radius: 2.2rem;
    position:relative;
    padding-left: 1rem;
    padding-right: 1.4rem;
    .point {
      display:none;
    }
    .text{
      font-size:0.9rem;
    }
    .icon{
      height: 0.6rem;
      display:none;
    }
  }
}
</style>
